import { collection, getDocs, limit, query, where } from "firebase/firestore"
import { db } from "./firebase"

/**
 * Converts a string into a URL-friendly slug.
 * Removes special characters, converts to lowercase, and replaces spaces with hyphens.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove non-word characters (except spaces and hyphens)
    .replace(/[\s_-]+/g, '-')   // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, '')    // Remove leading/trailing hyphens
}

/**
 * Ensures a slug is unique within a specific Firestore collection.
 * If a collision is found, it appends a number (e.g., "-1", "-2").
 */
export async function ensureUniqueSlug(
  collectionName: string, 
  base: string, 
  excludeId?: string
): Promise<string> {
  const baseSlug = slugify(base) || "item"
  
  for (let attempt = 0; attempt < 15; attempt++) {
    const candidate = attempt === 0 ? baseSlug : `${baseSlug}-${attempt}`
    
    // Check if this slug exists in the collection
    const q = query(
      collection(db, collectionName), 
      where("slug", "==", candidate), 
      limit(1)
    )
    
    const snap = await getDocs(q)
    const hit = snap.docs[0]
    
    // If no hit, or the hit is the item we are currently editing, the slug is safe
    if (!hit) return candidate
    if (excludeId && hit.id === excludeId) return candidate
  }
  
  // Fallback if somehow we can't find a unique one after 15 tries
  return `${baseSlug}-${Math.random().toString(36).slice(2, 7)}`
}
